import { useCallback, useContext, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { FieldSet, FormHelp, Label, LabeledControl, Legend, Radio } from '@zillow/constellation-10';
import { debounce } from '@zg-rentals/zdash';
import { propertyUtils } from '@zg-rentals/rental-manager-env';

import WizardServerError from '../../../../model/WizardServerError';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../Wizard/context';

import { useForm } from '../../../../hooks';

import PropertiesActions from '../../../../actions/PropertiesActions';
import PostingActions from '../../../../actions/PostingActions';
import actions from '../../../../containers/CreateListingPage/actions';
import wizardActions from '../../../Wizard/actions';

import { postingConstraints } from '../../../../utils/formValidation';

import TimeConstants from '../../../../constants/TimeConstants';
import { FOR_RENT_BY_KEY } from '../../../../containers/CreateListingPage/constants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';

import WizardAlert from '../../../WizardAlert';
import AdditionalBrokerInformation from '../../../AdditionalBrokerInformation';
import AgentLicenseType from '../../../AgentLicenseType';

const propTypes = {};
const defaultProps = {};

const radioControlName = 'forRentBy';
const brokerInformationControlName = 'isListingExclusive';
const agentLicenseTypeControlName = 'agentLicenseType';

const ForRentByFieldSet = () => {
  const dispatch = useDispatch();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { isInModalState, isSaveComplete, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const {
    isArchived,
    isFeed,
    isMultifamily,
    listingAlias,
    agentLicenseType,
    forRentBy,
    isListingExclusive,
    listingTypeCode,
    isUpdateAgentRestricted,
    isUpdateListingRestricted,
    isSelfServeOnboardingInProgress,
    isSSOBUpdateRestricted,
    canUpdateListing,
    currentProperty,
  } = useSelector(
    (state) => ({
      isArchived: state.properties.currentProperty.isArchived,
      isFeed: state.properties.currentProperty.isFeed,
      isMultifamily: state.properties.currentProperty.isMultifamily,
      listingAlias: state.properties.currentProperty.listingAlias,
      agentLicenseType: state.properties.currentProperty.agentLicenseType,
      forRentBy: state.properties.currentProperty.forRentBy,
      isListingExclusive: state.properties.currentProperty.isListingExclusive,
      listingTypeCode: state.properties.currentProperty.listingTypeCode,
      isUpdateAgentRestricted: state.properties.currentProperty.restrictions.isUpdateAgentRestricted,
      isUpdateListingRestricted: state.properties.currentProperty.restrictions.isUpdateListingRestricted,
      isSelfServeOnboardingInProgress:
        state.properties.currentProperty.selfServeOnboardingStatus.isSelfServeOnboardingInProgress,
      isSSOBUpdateRestricted: state.properties.currentProperty.selfServeOnboardingStatus.isUpdateRestricted,
      canUpdateListing: state.user.features.canUpdateListing,
      canUpdateAgent: state.user.features.canUpdateAgent,
      currentProperty: state.properties.currentProperty,
    }),
    shallowEqual,
  );

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const [isForRentByBroker, setIsForRentByBroker] = useState(forRentBy === 'agent');

  const isInputFormDisabled =
    isFeed ||
    !canUpdateListing ||
    isArchived ||
    isUpdateListingRestricted ||
    (isSelfServeOnboardingInProgress && isSSOBUpdateRestricted);

  const {
    inputs,
    errorObj,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      agentLicenseType,
      forRentBy,
      isListingExclusive,
    },
    postingConstraints,
  );

  useEffect(() => {
    if (!isMultifamily && isSaveComplete) {
      // if isUpdateAgentRestricted, only need to update `forRentBy`
      if (isUpdateAgentRestricted) {
        const mockEvent = {
          target: {
            name: radioControlName,
            value: inputs[radioControlName],
          },
        };

        save(mockEvent);
        return;
      }

      handleSaveMultipleInputs();
    }
  }, [isSaveComplete]);

  useEffect(() => {
    if (isForRentByBroker === false) {
      // only update form input to handle modal edit on save via isSaveComplete
      setFormInput({ name: brokerInformationControlName, value: false });
      setFormInput({ name: agentLicenseTypeControlName, value: null });
    }
  }, [isForRentByBroker]);

  const handleSaveMultipleInputs = () => {
    const promiseList = [];

    Object.entries(inputs).forEach(([key, value]) => {
      promiseList.push(
        save(
          {
            target: {
              name: key,
              value,
            },
          },
          true,
        ),
      );
    });

    Promise.allSettled(promiseList).then(() => {
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
    });
  };

  const setFormInput = ({ name, value }) => {
    handleFormChange({
      target: {
        name,
        value,
      },
    });
  };

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event, isSavingMultipleFields = false) => {
    const { value, name: fieldName } = event.target;

    // field error check needs to be above must click on cta check
    if (hasFieldError(fieldName, value)) {
      actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

      return Promise.resolve(false);
    }

    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return Promise.resolve(false);
    }

    const dataToSave = {
      fieldName,
      value,
      propertyId: listingAlias,
    };

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    return dispatch(PostingActions.property.edit(dataToSave))
      .then(() => {
        dispatch(PropertiesActions.updateCurrentProperty({ [fieldName]: value }));

        if (value === FOR_RENT_BY_KEY.AGENT) {
          const mockEvent = {
            target: {
              name: 'hidePhone',
              value: false,
            },
          };

          save(mockEvent);
        }

        // reset save cta
        if (isSavingMultipleFields === false) {
          actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
        }
      })
      .catch((message) => {
        updateServerError(message, fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const saveDebounced = useCallback(debounce(save, TimeConstants.SELECTION_DEBOUNCE_TIME_MS), []);

  const handleChange = (event) => {
    event.persist();
    setIsForRentByBroker(event.target.value === 'agent');

    clearServerError();
    handleFormChange(event);
    saveDebounced(event);
  };

  const handleBrokerInformationChange = ({ name = brokerInformationControlName, value }) => {
    const mockEvent = {
      target: {
        name,
        value,
      },
    };

    clearServerError();
    handleFormChange(mockEvent);
    save(mockEvent);
  };

  const handleAgentLicenseTypeChange = ({ name = agentLicenseTypeControlName, value }) => {
    // do not pass empty value to api request to prevent error
    if (value === '') {
      value = null;
    }

    const mockEvent = {
      target: {
        name,
        value,
      },
    };

    clearServerError();
    handleFormChange(mockEvent);
    save(mockEvent);
  };

  return (
    <div data-test="ForRentByFieldSet">
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      {!isRoomForRent && (
        <FieldSet
          disabled={isInputFormDisabled}
          legend={
            <Legend>
              {/* NOTE: `required` is moved into `legend` from `FieldSet` to avoid html required alert */}
              <Label required={!isInputFormDisabled}>Listed by</Label>
            </Legend>
          }
          error={!!errorObj.forRentBy}
          description={<FormHelp error={!!errorObj.forRentBy}>{errorObj.forRentBy}</FormHelp>}
        >
          <LabeledControl
            controlId="propertyOwner"
            label={<Label>Property owner</Label>}
            control={
              <Radio
                name={radioControlName}
                value="owner"
                checked={inputs.forRentBy === 'owner'}
                onChange={handleChange}
              />
            }
          />
          <LabeledControl
            controlId="managementCompanyOrBroker"
            label={<Label>Management company or broker</Label>}
            control={
              <Radio
                name={radioControlName}
                value="agent"
                checked={inputs.forRentBy === 'agent'}
                onChange={handleChange}
              />
            }
          />

          {!isMultifamily && (
            <LabeledControl
              controlId="tenant"
              label={<Label>Tenant</Label>}
              control={
                <Radio
                  name={radioControlName}
                  value="tenant"
                  checked={inputs.forRentBy === 'tenant'}
                  onChange={handleChange}
                />
              }
            />
          )}
        </FieldSet>
      )}

      {isForRentByBroker && !isUpdateAgentRestricted && (
        <>
          <FieldSet>
            <AdditionalBrokerInformation
              name={brokerInformationControlName}
              isListingExclusive={isListingExclusive}
              onChange={handleBrokerInformationChange}
            />
          </FieldSet>

          <FieldSet>
            <AgentLicenseType
              name={agentLicenseTypeControlName}
              agentLicenseType={agentLicenseType}
              onChange={handleAgentLicenseTypeChange}
            />
          </FieldSet>
        </>
      )}
    </div>
  );
};

ForRentByFieldSet.propTypes = propTypes;
ForRentByFieldSet.defaultProps = defaultProps;

export default ForRentByFieldSet;
